import type { LocationQuery, LocationQueryValue } from 'vue-router';

/** Query param that enables the pre-GA agency SRP experience (`?agency_agents=true`). */
export const AGENCY_AGENTS_QUERY_PARAM = 'agency_agents';

/** Common typo — rewritten to {@link AGENCY_AGENTS_QUERY_PARAM} in router and SSR. */
export const DEPRECATED_AGENCY_AGENT_QUERY_PARAM = 'agency_agent';

const isTruthyFeatureFlagValue = (value: LocationQueryValue | LocationQueryValue[] | undefined) => {
  if (Array.isArray(value)) {
    return value.includes('true');
  }

  return value === 'true';
};

const isAgencyAgentsTruthyInSearchParams = (params: URLSearchParams): boolean =>
  params.getAll(AGENCY_AGENTS_QUERY_PARAM).includes('true');

/** Rewrites `?agency_agent=` to `?agency_agents=` in a path+query URL for SSR. */
export const rewriteDeprecatedAgencyAgentsQueryInUrl = (url: string): string => {
  const queryIndex = url.indexOf('?');

  if (queryIndex === -1) {
    return url;
  }

  const path = url.slice(0, queryIndex);
  const params = new URLSearchParams(url.slice(queryIndex + 1));

  if (!params.has(DEPRECATED_AGENCY_AGENT_QUERY_PARAM) || params.has(AGENCY_AGENTS_QUERY_PARAM)) {
    return url;
  }

  const legacyValues = params.getAll(DEPRECATED_AGENCY_AGENT_QUERY_PARAM);
  params.delete(DEPRECATED_AGENCY_AGENT_QUERY_PARAM);

  for (const value of legacyValues) {
    params.append(AGENCY_AGENTS_QUERY_PARAM, value);
  }

  const search = params.toString();

  return search ? `${path}?${search}` : path;
};

export const isAgencyAgentsFeatureEnabledInLocationQuery = (query: LocationQuery): boolean =>
  isTruthyFeatureFlagValue(query[AGENCY_AGENTS_QUERY_PARAM]);

export const isAgencyAgentsFeatureEnabledInRequestUrl = (url: string): boolean => {
  const normalizedUrl = rewriteDeprecatedAgencyAgentsQueryInUrl(url);
  const queryIndex = normalizedUrl.indexOf('?');

  if (queryIndex === -1) {
    return false;
  }

  const params = new URLSearchParams(normalizedUrl.slice(queryIndex + 1));

  return isAgencyAgentsTruthyInSearchParams(params);
};

/**
 * Rewrites `agency_agent` → `agency_agents` when only the deprecated key is present.
 */
export const normalizeAgencyAgentsFeatureFlagQuery = (
  query: LocationQuery,
): LocationQuery | null => {
  if (query[DEPRECATED_AGENCY_AGENT_QUERY_PARAM] === undefined) {
    return null;
  }

  if (query[AGENCY_AGENTS_QUERY_PARAM] !== undefined) {
    return null;
  }

  const { [DEPRECATED_AGENCY_AGENT_QUERY_PARAM]: legacyValue, ...rest } = query;

  return {
    ...rest,
    [AGENCY_AGENTS_QUERY_PARAM]: legacyValue,
  };
};
